import { useEffect, useMemo, useState } from 'react';
import { showToast } from '@attentive-platform/stem-ui';

import { FormContainer } from '../../styled';
import { fetchApiData } from '../../Utils/ApiCalls';
import { ALL_BEARER_CALLBACKS, BEARER_CALLBACK } from '../../urls';

import BearerSearchSection from './components/BearerSearchSection';
import BearerCallbacksTable from './components/BearerCallbacksTable';

const normalizeTargetOptions = payload => {
  const list = Array.isArray(payload) ? payload : [];
  return list.map(o => ({
    label: o.name,
    value: o.id
  }));
};

const targetSignature = ids => [...(ids ?? [])].map(String).sort().join('|');

export const ThirdPartyCallbackManager = () => {
  const [rowSaved, setRowSaved] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [tableRows, setTableRows] = useState([]);
  const [savingIndex, setSavingIndex] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [targetOptions, setTargetOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [baselineSignatures, setBaselineSignatures] = useState([]);

  const loadTargetOptions = async () => {
    const { data, error } = await fetchApiData({
      endpoint: ALL_BEARER_CALLBACKS,
      apiRequest: 'GET'
    });
    if (error) {
      showToast(typeof error === 'string' ? error : 'Could not load integrations', 'error');
      setTargetOptions([]);
      return;
    }
    setTargetOptions(normalizeTargetOptions(data));
  };

  useEffect(() => {
    loadTargetOptions();
  }, []);

  const applySearchResult = rawList => {
    const normalized = rawList.map(r => ({
      user_id: r.user?.id ?? '',
      user_email: r.user?.email ?? '',
      targetIds: r.callback_targets?.map(t => t.id) ?? []
    }));
    setTableRows(normalized);
    setBaselineSignatures(normalized.map(r => targetSignature(r.targetIds)));
    setRowSaved(normalized.map(() => false));
    setHasSearched(true);
  };

  const handleSearch = async () => {
    const cid = companyId.trim();
    const email = userEmail.trim();

    if (!cid && !email) {
      showToast('Enter Company ID or User email', 'error');
      return;
    }

    setSearchLoading(true);

    const params = {};
    if (cid) params.company_id = cid;
    if (email) params.user_email = email;

    const { data, error } = await fetchApiData({
      endpoint: BEARER_CALLBACK,
      apiRequest: 'GET',
      params
    });
    setSearchLoading(false);

    if (error) {
      showToast(typeof error === 'string' ? error : 'Search failed', 'error');
      setTableRows([]);
      setBaselineSignatures([]);
      setRowSaved([]);
      setHasSearched(true);
      return;
    }

    applySearchResult(Array.isArray(data) ? data : data ? [data] : []);
  };

  const updateRowTargets = (index, ids) => {
    setTableRows(prev => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], targetIds: ids };
      return next;
    });
    setRowSaved(prev => {
      const next = [...prev];
      next[index] = false;
      return next;
    });
  };

  const rowDirty = useMemo(() => {
    return tableRows.map((r, i) => targetSignature(r.targetIds) !== (baselineSignatures[i] ?? ''));
  }, [tableRows, baselineSignatures]);

  const handleSaveRow = async index => {
    const row = tableRows[index];
    if (!row?.user_id) {
      showToast('Missing user id on row', 'error');
      return;
    }
    setSavingIndex(index);
    const { error } = await fetchApiData({
      endpoint: BEARER_CALLBACK,
      apiRequest: 'POST',
      data: {
        user_id: row.user_id,
        bearer_callback_target_ids: row.targetIds
      }
    });
    setSavingIndex(null);

    if (error) {
      showToast(typeof error === 'string' ? error : 'Update failed', 'error');
      return;
    }

    showToast('Integrations updated', 'success');
    setBaselineSignatures(prev => {
      const next = [...prev];
      next[index] = targetSignature(row.targetIds);
      return next;
    });
    setRowSaved(prev => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  return (
    <FormContainer>
      <BearerSearchSection
        companyId={companyId}
        userEmail={userEmail}
        onSearch={handleSearch}
        setCompanyId={setCompanyId}
        setUserEmail={setUserEmail}
        searchLoading={searchLoading}
      />

      {hasSearched && (
        <BearerCallbacksTable
          rowDirty={rowDirty}
          rowSaved={rowSaved}
          tableRows={tableRows}
          savingIndex={savingIndex}
          onSaveRow={handleSaveRow}
          targetOptions={targetOptions}
          onUpdateTargets={updateRowTargets}
        />
      )}
    </FormContainer>
  );
};

export default ThirdPartyCallbackManager;
